import { useThree, type ThreeEvent } from '@react-three/fiber';
import { Html, OrthographicCamera } from '@react-three/drei';
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore, type MutableRefObject } from 'react';
import {
  BoxGeometry,
  EdgesGeometry,
  Plane,
  Raycaster,
  Shape,
  Vector2,
  Vector3,
  Group,
  PlaneGeometry
} from 'three';

import { getObjectDefinition, getObjectRegistryVersion, subscribeObjectRegistry } from '../content/registry/objectRegistry';
import { renderAsset, type AssetRuntimeState } from '../content/registry/objectRenderers';
import { MapObjectErrorBoundary } from '../editor/components/MapObjectErrorBoundary';
import { useMapEditorStore } from '../editor/store/mapEditorStore';
import { TransformGizmo } from '../editor/components/TransformGizmo';
import { useEngineRuntimeStore } from '../store/engineRuntimeStore';
import { useLiveSceneStore } from './liveSceneStore';
import type { CharacterDefinition, CharacterPoseLibrary } from '../content/schema/character';
import type {
  MapCharacterInstance,
  MapDocument,
  MapFloorDocument,
  MapFloorLevel,
  MapFloorPanel,
  MapObjectInstance,
  MapWallSegment,
  MapWallTrimFaces,
} from '../content/schema/map';
import { floorWorldY, isFloorLevelVisible, type MapRuntimeOptions } from './mapRuntime';
import { clearDropPlacementResolver, setDropPlacementResolver } from './dropPlacement';
import { PlacedCharacters } from './PlacedCharacters';
import type { CutsceneActor } from './CutscenePlayer';
import { shadeColor, surfaceMaterial } from './surfaces';
import {
  CORNICE_HEIGHT,
  DADO_HEIGHT,
  SKIRTING_HEIGHT,
  TRIM_PROJECTION,
  wallTrimProfilePoints,
  wallTrimRun,
} from './wallTrim';

// ── Map Builder: ortho top-down structure-editing viewport ─────────────────
// Additive import block (kept separate from the ones above so this whole
// addition - see the matching block at the bottom of the file - is easy to
// isolate from concurrent edits elsewhere in this file).
import type { MapRoomDraft, MapStairRun, MapStairTrigger } from '../content/schema/map';
import { blueprintScale, type StairFootprint } from '../editor/blueprintTools';
import { Viewport } from './viewport/Viewport';

/**
 * Registers a resolver (current camera + current floor) so the editor shell's
 * drag-drop handler can raycast a dropped asset onto the current floor plane.
 */
function DropPlacementController({ runtimeOptions }: { runtimeOptions?: MapRuntimeOptions }) {
  const { camera, gl } = useThree();
  const currentFloor = useEngineRuntimeStore((state) => state.currentFloor);

  useEffect(() => {
    const raycaster = new Raycaster();
    const ndc = new Vector2();
    const plane = new Plane(new Vector3(0, 1, 0), 0);
    const hit = new Vector3();
    const resolver = (clientX: number, clientY: number) => {
      const rect = gl.domElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      plane.constant = -floorWorldY(currentFloor as MapFloorLevel, runtimeOptions);
      if (!raycaster.ray.intersectPlane(plane, hit)) return null;
      return { point: [hit.x, 0, hit.z] as [number, number, number], floor: currentFloor };
    };
    setDropPlacementResolver(resolver);
    return () => clearDropPlacementResolver(resolver);
  }, [camera, gl, currentFloor, runtimeOptions]);

  useEffect(() => {
    const fn = (worldPos: [number, number, number]) => {
      const v = new Vector3(worldPos[0], worldPos[1], worldPos[2]);
      v.project(camera);
      const rect = gl.domElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      const x = rect.left + (v.x + 1) * rect.width / 2;
      const y = rect.top + (-v.y + 1) * rect.height / 2;
      return [x, y] as [number, number];
    };
    useMapEditorStore.getState().setProjectToScreen(fn);
    return () => useMapEditorStore.getState().setProjectToScreen(null);
  }, [camera, gl]);

  return null;
}

const OUTLINE_COLORS = {
  selectedSingle: '#ffcf6b',
  selectedMulti: '#82d2ff',
  hover: '#cfd4da',
};

const NEUTRAL_FLOOR = '#3a4048';
const NEUTRAL_WALL = '#4a525c';

// ── Selection helpers ────────────────────────────────────────────────────────

function OutlineBox({
  size,
  color,
  opacity = 1,
}: {
  size: [number, number, number];
  color: string;
  opacity?: number;
}) {
  const [sizeX, sizeY, sizeZ] = size;
  const edges = useMemo(
    () => new EdgesGeometry(new BoxGeometry(sizeX, sizeY, sizeZ)),
    [sizeX, sizeY, sizeZ],
  );
  useEffect(() => () => edges.dispose(), [edges]);
  return (
    <lineSegments geometry={edges}>
      <lineBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </lineSegments>
  );
}

function GroundRing({ radius, color }: { radius: number; color: string }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
      <ringGeometry args={[radius - 0.05, radius, 48]} />
      <meshBasicMaterial color={color} transparent opacity={0.55} depthTest={false} />
    </mesh>
  );
}

// ── Non-prop helpers & components ───────────────────────────────────────────

import type { MapLight, MapTriggerVolume, MapDecal, MapRoomMarker } from '../content/schema/map';

function SceneLight({
  light,
  editable,
  selected,
  hovered,
}: {
  light: MapLight;
  editable: boolean;
  selected: boolean;
  hovered: boolean;
}) {
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(light.id);
    } else {
      selectObject(light.id);
    }
    beginDrag(light.id, 'Move Light');
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    setHoveredObjectId(light.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === light.id) setHoveredObjectId(null);
  };

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    _rot: [number, number, number],
    _scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextLights = store.lights.map((l) => {
      if (l.id !== light.id) return l;
      return {
        ...l,
        position: [Number(pos[0].toFixed(3)), Number(pos[1].toFixed(3)), Number(pos[2].toFixed(3))] as [number, number, number],
      };
    });
    useMapEditorStore.setState({ lights: nextLights });
  }, [light.id]);

  const handleGizmoDragStart = useCallback(() => {
    beginDrag(light.id, 'Transform Light');
  }, [beginDrag, light.id]);

  const renderLightSource = () => {
    if (light.kind === 'spot') {
      return (
        <spotLight
          position={[0, 0, 0]}
          color={light.color}
          intensity={light.intensity}
          distance={light.range ?? 8}
          angle={light.angle ?? 0.5}
          castShadow={light.castShadow ?? false}
        />
      );
    }
    if (light.kind === 'ambient-override') {
      return <ambientLight color={light.color} intensity={light.intensity} />;
    }
    return (
      <pointLight
        position={[0, 0, 0]}
        color={light.color}
        intensity={light.intensity}
        distance={light.range ?? 8}
        castShadow={light.castShadow ?? false}
      />
    );
  };

  return (
    <group
      ref={groupRef}
      position={light.position}
      onPointerDown={editable ? handlePointerDown : undefined}
      onPointerOver={editable ? handlePointerOver : undefined}
      onPointerOut={editable ? handlePointerOut : undefined}
    >
      {renderLightSource()}

      {editable && (
        <>
          <mesh>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshBasicMaterial color={light.color} />
          </mesh>
          <mesh scale={[1.2, 1.2, 1.2]}>
            <sphereGeometry args={[0.15, 16, 16]} />
            <meshBasicMaterial color={selected ? '#ffcf6b' : hovered ? '#ffffff' : '#888888'} wireframe transparent opacity={0.6} />
          </mesh>

          {selected && (
            <>
              {light.kind === 'point' && (
                <mesh>
                  <sphereGeometry args={[light.range ?? 8, 32, 32]} />
                  <meshBasicMaterial wireframe color={light.color} transparent opacity={0.08} depthWrite={false} />
                </mesh>
              )}
              {light.kind === 'spot' && (
                <group rotation={[Math.PI / 2, 0, 0]}>
                  <mesh position={[0, -(light.range ?? 8) / 2, 0]}>
                    <coneGeometry args={[(light.range ?? 8) * Math.tan(light.angle ?? 0.5), light.range ?? 8, 16, 1, true]} />
                    <meshBasicMaterial wireframe color={light.color} transparent opacity={0.08} depthWrite={false} />
                  </mesh>
                </group>
              )}
            </>
          )}

          {selected && gizmoMode !== 'off' && (
            <TransformGizmo
              mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
              objectRef={groupRef}
              onTransform={handleGizmoTransform}
              onDragStart={handleGizmoDragStart}
            />
          )}
        </>
      )}
    </group>
  );
}

function SceneTrigger({
  trigger,
  editable,
  selected,
  hovered,
}: {
  trigger: MapTriggerVolume;
  editable: boolean;
  selected: boolean;
  hovered: boolean;
}) {
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(trigger.id);
    } else {
      selectObject(trigger.id);
    }
    beginDrag(trigger.id, 'Move Trigger');
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    setHoveredObjectId(trigger.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === trigger.id) setHoveredObjectId(null);
  };

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    _rot: [number, number, number],
    scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextTriggers = store.triggers.map((t) => {
      if (t.id !== trigger.id) return t;
      return {
        ...t,
        center: [Number(pos[0].toFixed(3)), Number(pos[2].toFixed(3))] as [number, number],
        size: [
          Number(Math.max(0.1, trigger.size[0] * scale[0]).toFixed(3)),
          Number(Math.max(0.1, trigger.size[1] * scale[2]).toFixed(3)),
        ] as [number, number],
        height: Number(Math.max(0.1, trigger.height * scale[1]).toFixed(3)),
      };
    });
    if (groupRef.current) {
      groupRef.current.scale.set(1, 1, 1);
    }
    useMapEditorStore.setState({ triggers: nextTriggers });
  }, [trigger.id, trigger.size, trigger.height]);

  const handleGizmoDragStart = useCallback(() => {
    beginDrag(trigger.id, 'Transform Trigger');
  }, [beginDrag, trigger.id]);

  const outlineColor = selected ? '#32cd32' : hovered ? '#85e585' : '#4ba34b';

  return (
    <group
      ref={groupRef}
      position={[trigger.center[0], trigger.height / 2, trigger.center[1]]}
      onPointerDown={editable ? handlePointerDown : undefined}
      onPointerOver={editable ? handlePointerOver : undefined}
      onPointerOut={editable ? handlePointerOut : undefined}
    >
      {editable && (
        <>
          <mesh>
            <boxGeometry args={[trigger.size[0], trigger.height, trigger.size[1]]} />
            <meshBasicMaterial color="#32cd32" transparent opacity={0.25} depthWrite={false} />
          </mesh>

          <OutlineBox size={[trigger.size[0], trigger.height, trigger.size[1]]} color={outlineColor} opacity={selected ? 0.9 : 0.4} />

          {selected && (
            <Html position={[0, trigger.height / 2 + 0.3, 0]} center style={{ pointerEvents: 'none' }}>
              <div className="editor-floating-label" style={{ borderLeftColor: '#32cd32' }}>
                <span className="editor-floating-label-name">Trigger ({trigger.kind})</span>
                <span className="editor-floating-label-id">{trigger.id.split('_').slice(-1)[0]}</span>
              </div>
            </Html>
          )}

          {selected && gizmoMode !== 'off' && (
            <TransformGizmo
              mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
              objectRef={groupRef}
              onTransform={handleGizmoTransform}
              onDragStart={handleGizmoDragStart}
            />
          )}
        </>
      )}
    </group>
  );
}

function SceneDecal({
  decal,
  editable,
  selected,
  hovered,
}: {
  decal: MapDecal;
  editable: boolean;
  selected: boolean;
  hovered: boolean;
}) {
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(decal.id);
    } else {
      selectObject(decal.id);
    }
    beginDrag(decal.id, 'Move Decal');
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    setHoveredObjectId(decal.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === decal.id) setHoveredObjectId(null);
  };

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    rot: [number, number, number],
    scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextDecals = store.decals.map((d) => {
      if (d.id !== decal.id) return d;
      return {
        ...d,
        position: [Number(pos[0].toFixed(3)), Number(pos[1].toFixed(3)), Number(pos[2].toFixed(3))] as [number, number, number],
        rotation: [Number(rot[0].toFixed(3)), Number(rot[1].toFixed(3)), Number(rot[2].toFixed(3))] as [number, number, number],
        size: [
          Number(Math.max(0.1, decal.size[0] * scale[0]).toFixed(3)),
          Number(Math.max(0.1, decal.size[1] * scale[2]).toFixed(3)),
        ] as [number, number],
      };
    });
    if (groupRef.current) {
      groupRef.current.scale.set(1, 1, 1);
    }
    useMapEditorStore.setState({ decals: nextDecals });
  }, [decal.id, decal.size]);

  const handleGizmoDragStart = useCallback(() => {
    beginDrag(decal.id, 'Transform Decal');
  }, [beginDrag, decal.id]);

  let color = '#0055ff';
  let roughness = 0.1;
  let metalness = 0.8;
  if (decal.material.includes('stain')) {
    color = '#4a0e0e';
    roughness = 0.8;
    metalness = 0.1;
  } else if (decal.material.includes('crack')) {
    color = '#1c1c1c';
    roughness = 0.9;
    metalness = 0.0;
  }

  const outlineColor = selected ? '#ffcf6b' : hovered ? '#ffffff' : '#888888';
  const [decalWidth, decalHeight] = decal.size;
  const edgesGeo = useMemo(() => new EdgesGeometry(new PlaneGeometry(decalWidth, decalHeight)), [decalWidth, decalHeight]);
  useEffect(() => () => edgesGeo.dispose(), [edgesGeo]);

  return (
    <group
      ref={groupRef}
      position={decal.position}
      rotation={decal.rotation ?? [0, 0, 0]}
      onPointerDown={editable ? handlePointerDown : undefined}
      onPointerOver={editable ? handlePointerOver : undefined}
      onPointerOut={editable ? handlePointerOut : undefined}
    >
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <planeGeometry args={decal.size} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={decal.opacity ?? 0.7}
          roughness={roughness}
          metalness={metalness}
          depthWrite={false}
        />
      </mesh>

      {editable && (
        <>
          <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}>
            <lineSegments geometry={edgesGeo}>
              <lineBasicMaterial color={outlineColor} transparent opacity={selected ? 0.9 : 0.4} />
            </lineSegments>
          </group>

          {selected && (
            <Html position={[0, 0.2, 0]} center style={{ pointerEvents: 'none' }}>
              <div className="editor-floating-label" style={{ borderLeftColor: color }}>
                <span className="editor-floating-label-name">Decal ({decal.material})</span>
              </div>
            </Html>
          )}

          {selected && gizmoMode !== 'off' && (
            <TransformGizmo
              mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
              objectRef={groupRef}
              onTransform={handleGizmoTransform}
              onDragStart={handleGizmoDragStart}
            />
          )}
        </>
      )}
    </group>
  );
}

function SceneMarker({
  marker,
  editable,
  selected,
  hovered,
}: {
  marker: MapRoomMarker;
  editable: boolean;
  selected: boolean;
  hovered: boolean;
}) {
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(marker.id);
    } else {
      selectObject(marker.id);
    }
    beginDrag(marker.id, 'Move Marker');
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    setHoveredObjectId(marker.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === marker.id) setHoveredObjectId(null);
  };

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    _rot: [number, number, number],
    _scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextMarkers = store.markers.map((m) => {
      if (m.id !== marker.id) return m;
      return {
        ...m,
        position: [Number(pos[0].toFixed(3)), Number(pos[2].toFixed(3))] as [number, number],
      };
    });
    useMapEditorStore.setState({ markers: nextMarkers });
  }, [marker.id]);

  const handleGizmoDragStart = useCallback(() => {
    beginDrag(marker.id, 'Transform Marker');
  }, [beginDrag, marker.id]);

  let color = '#ff007f';
  if (marker.kind === 'spawn') color = '#00ffcc';
  else if (marker.kind === 'event') color = '#ffcc00';

  const outlineColor = selected ? '#ffcf6b' : hovered ? '#ffffff' : '#888888';

  return (
    <group
      ref={groupRef}
      position={[marker.position[0], 0, marker.position[1]]}
      onPointerDown={editable ? handlePointerDown : undefined}
      onPointerOver={editable ? handlePointerOver : undefined}
      onPointerOut={editable ? handlePointerOut : undefined}
    >
      <mesh position={[0, 0.4, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.8, 8]} />
        <meshStandardMaterial color="#888888" roughness={0.5} />
      </mesh>

      <mesh position={[0.15, 0.7, 0]}>
        <boxGeometry args={[0.3, 0.2, 0.02]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} />
      </mesh>

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
        <ringGeometry args={[0.2, 0.25, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.6} />
      </mesh>

      {editable && (
        <>
          <group position={[0, 0.4, 0]}>
            <OutlineBox size={[0.5, 0.8, 0.5]} color={outlineColor} opacity={selected ? 0.9 : 0.4} />
          </group>

          {selected && (
            <Html position={[0, 1.1, 0]} center style={{ pointerEvents: 'none' }}>
              <div className="editor-floating-label" style={{ borderLeftColor: color }}>
                <span className="editor-floating-label-name">{marker.name} ({marker.kind})</span>
              </div>
            </Html>
          )}

          {selected && gizmoMode !== 'off' && (
            <TransformGizmo
              mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
              objectRef={groupRef}
              onTransform={handleGizmoTransform}
              onDragStart={handleGizmoDragStart}
            />
          )}
        </>
      )}
    </group>
  );
}

// ── Character selection + TransformControls (edit mode) ────────────────────
// A sibling interaction layer for `PlacedCharacters` (see LevelRenderer's
// mount below), not a replacement for it: PlacedCharacters/PlacedCharacter
// stay the always-on, patrol-aware *visual* renderer in every mode exactly
// as before. This component only adds the pointer/selection/gizmo surface
// the other five entity types already have, following their exact shape
// (selectObject/toggleSelectObject/beginDrag/setHoveredObjectId/gizmoMode,
// TransformGizmo bound to a groupRef) - it renders an invisible hit target
// plus a selection outline at the same position, and writes the dragged
// position/facing back into `characterInstances` on the store.
function SceneCharacterSelection({
  instance,
  name,
  floorOffsetY,
  selected,
  hovered,
}: {
  instance: MapCharacterInstance;
  name: string;
  floorOffsetY: number;
  selected: boolean;
  hovered: boolean;
}) {
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(instance.id);
    } else {
      selectObject(instance.id);
    }
    beginDrag(instance.id, 'Move Character');
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    setHoveredObjectId(instance.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === instance.id) setHoveredObjectId(null);
  };

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    rot: [number, number, number],
    _scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextInstances = store.characterInstances.map((i) => {
      if (i.id !== instance.id) return i;
      return {
        ...i,
        position: [
          Number(pos[0].toFixed(3)),
          Number((pos[1] - floorOffsetY).toFixed(3)),
          Number(pos[2].toFixed(3)),
        ] as [number, number, number],
        rotationY: Number(rot[1].toFixed(3)),
      };
    });
    useMapEditorStore.setState({ characterInstances: nextInstances });
  }, [instance.id, floorOffsetY]);

  const handleGizmoDragStart = useCallback(() => {
    beginDrag(instance.id, 'Transform Character');
  }, [beginDrag, instance.id]);

  const outlineColor = selected ? OUTLINE_COLORS.selectedSingle : hovered ? OUTLINE_COLORS.hover : '#888888';
  const hitSize: [number, number, number] = [0.6, 1.8, 0.6];

  return (
    <group
      ref={groupRef}
      position={[instance.position[0], instance.position[1] + floorOffsetY, instance.position[2]]}
      rotation={[0, instance.rotationY ?? 0, 0]}
      onPointerDown={handlePointerDown}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <mesh position={[0, hitSize[1] / 2, 0]}>
        <boxGeometry args={hitSize} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {(selected || hovered) && (
        <group position={[0, hitSize[1] / 2, 0]} renderOrder={10}>
          <OutlineBox size={hitSize} color={outlineColor} opacity={selected ? 0.95 : 0.6} />
        </group>
      )}

      {selected && (
        <Html
          position={[0, hitSize[1] + 0.2, 0]}
          center
          zIndexRange={[20, 10]}
          wrapperClass="editor-floating-label-wrapper"
          style={{ pointerEvents: 'none' }}
        >
          <div className="editor-floating-label">
            <span className="editor-floating-label-name">{name}</span>
            <span className="editor-floating-label-id">{instance.id.split('_').slice(-1)[0]}</span>
          </div>
        </Html>
      )}

      {selected && gizmoMode !== 'off' && (
        <TransformGizmo
          mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
          objectRef={groupRef}
          onTransform={handleGizmoTransform}
          onDragStart={handleGizmoDragStart}
        />
      )}
    </group>
  );
}
// ── End character selection + TransformControls ─────────────────────────────

// ── Object instance (editable or static) ──────────────────────────────────────

function SceneObject({
  instance,
  floorOffsetY,
  editable,
  selected,
  isMultiSelect,
  hovered,
  runtimeState,
}: {
  instance: MapObjectInstance;
  floorOffsetY: number;
  editable: boolean;
  selected: boolean;
  isMultiSelect: boolean;
  hovered: boolean;
  runtimeState: AssetRuntimeState;
}) {
  const definition = getObjectDefinition(instance.assetId);
  const selectObject = useMapEditorStore((state) => state.selectObject);
  const toggleSelectObject = useMapEditorStore((state) => state.toggleSelectObject);
  const beginDrag = useMapEditorStore((state) => state.beginDrag);
  const setHoveredObjectId = useMapEditorStore((state) => state.setHoveredObjectId);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const groupRef = useRef<Group>(null);

  const handleGizmoTransform = useCallback((
    pos: [number, number, number],
    rot: [number, number, number],
    scale: [number, number, number]
  ) => {
    const store = useMapEditorStore.getState();
    const nextInstances = store.objectInstances.map((inst) => {
      if (inst.id !== instance.id) return inst;
      return {
        ...inst,
        transform: {
          position: [Number(pos[0].toFixed(3)), Number(pos[1].toFixed(3)), Number(pos[2].toFixed(3))] as [number, number, number],
          rotation: [Number(rot[0].toFixed(3)), Number(rot[1].toFixed(3)), Number(rot[2].toFixed(3))] as [number, number, number],
          scale: [Number(scale[0].toFixed(3)), Number(scale[1].toFixed(3)), Number(scale[2].toFixed(3))] as [number, number, number],
        },
      };
    });
    useMapEditorStore.setState({ objectInstances: nextInstances });
  }, [instance.id]);

  const handleGizmoDragStart = useCallback(() => {
    const store = useMapEditorStore.getState();
    const ids = store.selectedObjectIds.length > 0 ? store.selectedObjectIds : [instance.id];
    beginDrag(ids, 'Transform object');
  }, [beginDrag, instance.id]);

  const dragOffset = useMemo(() => new Vector3(), []);
  const dragPlaneRef = useRef(new Plane(new Vector3(0, 1, 0), 0));
  const dragPoint = useMemo(() => new Vector3(), []);
  const draggingRef = useRef(false);
  const dragIdsRef = useRef<string[]>([]);
  const dragStartPositionsRef = useRef<Map<string, [number, number, number]>>(new Map());

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();

    const store = useMapEditorStore.getState();
    const wasInSelection = store.selectedObjectIds.includes(instance.id);

    if (event.nativeEvent.shiftKey) {
      toggleSelectObject(instance.id);
      return;
    }
    if (!wasInSelection || store.selectedObjectIds.length <= 1) {
      selectObject(instance.id);
    }

    const nextState = useMapEditorStore.getState();
    const draggingIds = nextState.selectedObjectIds.length > 0 ? nextState.selectedObjectIds : [instance.id];
    dragIdsRef.current = draggingIds;
    const positions = new Map<string, [number, number, number]>();
    for (const id of draggingIds) {
      const inst = nextState.objectInstances.find((i) => i.id === id);
      if (inst) positions.set(id, [...inst.transform.position] as [number, number, number]);
    }
    dragStartPositionsRef.current = positions;
    dragPlaneRef.current.constant = -(floorOffsetY + instance.transform.position[1]);

    beginDrag(draggingIds, draggingIds.length > 1 ? `Move ${draggingIds.length} objects` : 'Move object');
    draggingRef.current = true;
    (event.target as Element | null)?.setPointerCapture?.(event.pointerId);
    if (event.ray.intersectPlane(dragPlaneRef.current, dragPoint)) {
      dragOffset.set(
        instance.transform.position[0] - dragPoint.x,
        0,
        instance.transform.position[2] - dragPoint.z,
      );
    }
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (!editable || !draggingRef.current) return;
    event.stopPropagation();
    if (!event.ray.intersectPlane(dragPlaneRef.current, dragPoint)) return;

    const axisLock = useMapEditorStore.getState().axisLock;
    const anchor = dragStartPositionsRef.current.get(instance.id);
    if (!anchor) return;

    const desiredX = dragPoint.x + dragOffset.x;
    const desiredZ = dragPoint.z + dragOffset.z;
    const effectiveX = axisLock === 'z' ? anchor[0] : desiredX;
    const effectiveZ = axisLock === 'x' ? anchor[2] : desiredZ;
    const delta: [number, number, number] = [effectiveX - anchor[0], 0, effectiveZ - anchor[2]];

    const ids = dragIdsRef.current;
    if (ids.length > 1) {
      const store = useMapEditorStore.getState();
      useMapEditorStore.setState({
        objectInstances: store.objectInstances.map((inst) => {
          if (!ids.includes(inst.id)) return inst;
          const base = dragStartPositionsRef.current.get(inst.id);
          if (!base) return inst;
          return {
            ...inst,
            transform: {
              ...inst.transform,
              position: [
                Number((base[0] + delta[0]).toFixed(3)),
                inst.transform.position[1],
                Number((base[2] + delta[2]).toFixed(3)),
              ] as [number, number, number],
            },
          };
        }),
      });
    } else {
      useMapEditorStore.setState((state) => ({
        objectInstances: state.objectInstances.map((inst) =>
          inst.id === instance.id
            ? {
                ...inst,
                transform: {
                  ...inst.transform,
                  position: [
                    Number((anchor[0] + delta[0]).toFixed(3)),
                    inst.transform.position[1],
                    Number((anchor[2] + delta[2]).toFixed(3)),
                  ] as [number, number, number],
                },
              }
            : inst,
        ),
      }));
    }
  };

  const handlePointerUp = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    draggingRef.current = false;
    dragIdsRef.current = [];
    dragStartPositionsRef.current = new Map();
    (event.target as Element | null)?.releasePointerCapture?.(event.pointerId);
  };

  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    setHoveredObjectId(instance.id);
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    if (!editable) return;
    event.stopPropagation();
    if (useMapEditorStore.getState().hoveredObjectId === instance.id) setHoveredObjectId(null);
  };

  if (!definition) {
    // Unknown asset → still render the neutral fallback gizmo from the registry.
    return (
      <group position={instance.transform.position} rotation={instance.transform.rotation} scale={instance.transform.scale}>
        <MapObjectErrorBoundary instance={instance}>
          {renderAsset({ ...instance, transform: { position: [0, 0, 0] } }, runtimeState)}
        </MapObjectErrorBoundary>
      </group>
    );
  }

  const outlineColor = selected
    ? isMultiSelect
      ? OUTLINE_COLORS.selectedMulti
      : OUTLINE_COLORS.selectedSingle
    : OUTLINE_COLORS.hover;
  const showOutline = selected || (hovered && editable);
  const showLabel = selected && !isMultiSelect;
  const pivotBaseCenter = definition.editor.pivot === 'base-center';
  const centerY = pivotBaseCenter ? definition.bounds.size[1] / 2 : 0;
  const groundRadius = Math.max(definition.bounds.size[0], definition.bounds.size[2]) * 0.6;
  const displayName = instance.name ?? definition.name;

  return (
    <group
      ref={groupRef}
      position={instance.transform.position}
      rotation={instance.transform.rotation}
      scale={instance.transform.scale}
      onPointerDown={editable ? handlePointerDown : undefined}
      onPointerMove={editable ? handlePointerMove : undefined}
      onPointerUp={editable ? handlePointerUp : undefined}
      onPointerOver={editable ? handlePointerOver : undefined}
      onPointerOut={editable ? handlePointerOut : undefined}
    >
      <MapObjectErrorBoundary instance={instance}>
        {renderAsset(
          { ...instance, transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] } },
          runtimeState,
        )}
      </MapObjectErrorBoundary>

      {showOutline ? (
        <group position={[0, centerY, 0]} renderOrder={10}>
          <OutlineBox size={definition.bounds.size} color={outlineColor} opacity={selected ? 0.95 : 0.6} />
        </group>
      ) : null}

      {selected ? <GroundRing radius={groundRadius} color={outlineColor} /> : null}

      {showLabel ? (
        <Html
          position={[0, definition.bounds.size[1] + 0.2, 0]}
          center
          zIndexRange={[20, 10]}
          wrapperClass="editor-floating-label-wrapper"
          style={{ pointerEvents: 'none' }}
        >
          <div className="editor-floating-label">
            <span className="editor-floating-label-name">{displayName}</span>
            <span className="editor-floating-label-id">{instance.id.split('-').slice(-1)[0]}</span>
          </div>
        </Html>
      ) : null}

      {selected && editable && gizmoMode !== 'off' ? (
        <TransformGizmo
          mode={gizmoMode as 'translate' | 'rotate' | 'scale'}
          objectRef={groupRef}
          onTransform={handleGizmoTransform}
          onDragStart={handleGizmoDragStart}
        />
      ) : null}
    </group>
  );
}

// ── Placement surface (click-to-place when a palette item is pending) ──────────

function PlacementSurface({ floor, panel }: { floor: MapFloorLevel; panel: MapFloorPanel }) {
  const pendingAssetId = useMapEditorStore((state) => state.pendingAssetId);
  const setPlacementPreview = useMapEditorStore((state) => state.setPlacementPreview);
  const addObject = useMapEditorStore((state) => state.addObject);
  const clearSelection = useMapEditorStore((state) => state.clearSelection);
  const setPendingAssetId = useMapEditorStore((state) => state.setPendingAssetId);

  return (
    <mesh
      position={[panel.center[0], 0.02, panel.center[1]]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerMove={(event) => {
        useMapEditorStore.getState().setLastRaycastHit([event.point.x, event.point.z]);
        if (!pendingAssetId) return;
        event.stopPropagation();
        setPlacementPreview({ floor, position: [event.point.x, 0, event.point.z] });
      }}
      onPointerLeave={() => setPlacementPreview(null)}
      onPointerDown={(event) => {
        if (event.nativeEvent.button === 2) {
          event.stopPropagation();
          if (pendingAssetId) setPendingAssetId(null);
          return;
        }
        event.stopPropagation();
        if (pendingAssetId) {
          addObject(pendingAssetId, floor, [event.point.x, 0, event.point.z]);
          return;
        }
        clearSelection();
      }}
    >
      <planeGeometry args={panel.size} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

function GhostPreviewProp({
  assetId,
  position,
  rotationY,
  runtimeState,
}: {
  assetId: string;
  position: [number, number, number];
  rotationY: number;
  runtimeState: AssetRuntimeState;
}) {
  const definition = getObjectDefinition(assetId);
  if (!definition) return null;
  const ghostInstance: MapObjectInstance = {
    id: `ghost-${assetId}`,
    assetId,
    floor: 0,
    transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
    params: definition.defaultParams ? { ...definition.defaultParams } : undefined,
  };
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <group>{renderAsset(ghostInstance, runtimeState)}</group>
      <group position={[0, definition.editor.pivot === 'base-center' ? definition.bounds.size[1] / 2 : 0, 0]}>
        <OutlineBox size={definition.bounds.size} color="#7fe3ff" opacity={0.5} />
      </group>
    </group>
  );
}

// ── Floor geometry from the document ───────────────────────────────────────────

/**
 * Generated wall trim: skirting, cornice and dado rail derived from the wall's
 * own extents, so a document only says "trimmed" rather than authoring six
 * extra boxes per wall. Trim runs along the wall's long axis and projects
 * slightly proud of it - of both faces by default, or of one named face when
 * the trim asks for it (`MapWallTrimFaces`, for an exterior wall whose other
 * face is buried in an outside skin). The measurements are `wallTrim.ts`.
 */
function WallTrim({ wall, baseColor }: { wall: MapWallSegment; baseColor: string }) {
  const height = wall.size[1];
  const trim = wall.trim;
  if (!trim) return null;
  const color = trim.color ?? shadeColor(baseColor, 0.62);

  if (trim.profile === 'ovolo') {
    return (
      <>
        {trim.skirting ? (
          <TrimMember
            wallSize={wall.size}
            faces={trim.faces}
            memberHeight={SKIRTING_HEIGHT}
            baseY={-height / 2}
            color={color}
            roughness={0.72}
            metalness={0.03}
          />
        ) : null}
        {trim.cornice ? (
          <TrimMember
            wallSize={wall.size}
            faces={trim.faces}
            memberHeight={CORNICE_HEIGHT}
            baseY={height / 2 - CORNICE_HEIGHT}
            color={color}
            roughness={0.78}
            metalness={0.02}
          />
        ) : null}
        {typeof trim.dadoHeight === 'number' ? (
          <TrimMember
            wallSize={wall.size}
            faces={trim.faces}
            memberHeight={DADO_HEIGHT}
            baseY={-height / 2 + trim.dadoHeight - DADO_HEIGHT / 2}
            color={color}
            roughness={0.7}
            metalness={0.03}
          />
        ) : null}
      </>
    );
  }

  const run = wallTrimRun(wall.size, trim.faces);
  const [runX, runZ] = run.size;
  const [offsetX, offsetZ] = run.offset;

  return (
    <>
      {trim.skirting ? (
        <mesh
          position={[offsetX, -height / 2 + SKIRTING_HEIGHT / 2, offsetZ]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[runX, SKIRTING_HEIGHT, runZ]} />
          <meshStandardMaterial color={color} roughness={0.72} metalness={0.03} />
        </mesh>
      ) : null}
      {trim.cornice ? (
        <mesh
          position={[offsetX, height / 2 - CORNICE_HEIGHT / 2, offsetZ]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[runX, CORNICE_HEIGHT, runZ]} />
          <meshStandardMaterial color={color} roughness={0.78} metalness={0.02} />
        </mesh>
      ) : null}
      {typeof trim.dadoHeight === 'number' ? (
        <mesh
          position={[offsetX, -height / 2 + trim.dadoHeight, offsetZ]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[runX, DADO_HEIGHT, runZ]} />
          <meshStandardMaterial color={color} roughness={0.7} metalness={0.03} />
        </mesh>
      ) : null}
    </>
  );
}

/**
 * One skirting, cornice or dado member cut to the `'ovolo'` profile, drawn
 * once per active face - one mesh for a named face, two for the both-faces
 * default. Each mesh is the moulding's own cross-section (`wallTrimProfilePoints`)
 * extruded along the run and stood flush against the wall's face plane,
 * rather than the box trim's approach of one shape spanning the wall's
 * full thickness plus a projection at each end: a curved profile has no
 * need to duplicate geometry the wall's own box already draws between its
 * two faces, so each face gets its own flush-mounted piece instead.
 *
 * The position and rotation per face are the profile counterpart of
 * `wallTrimRun`'s box footprint - same axis (the wall's thin axis carries
 * the projection, the other carries the run) and the same sign convention
 * for `faces`, worked out so the shape's local (projection, height) plane
 * lands in the wall's own X/Z/Y frame with its foot against the correct
 * face and its run centred on the wall like the box was.
 */
function TrimMember({
  wallSize,
  faces,
  memberHeight,
  baseY,
  color,
  roughness,
  metalness,
}: {
  wallSize: readonly [number, number, number];
  faces: MapWallTrimFaces | undefined;
  memberHeight: number;
  baseY: number;
  color: string;
  roughness: number;
  metalness: number;
}) {
  const [width, , depth] = wallSize;
  const shape = useMemo(() => {
    const points = wallTrimProfilePoints(memberHeight, TRIM_PROJECTION);
    return new Shape(points.map(([x, y]) => new Vector2(x, y)));
  }, [memberHeight]);
  const axis: 'x' | 'z' = faces === '+x' || faces === '-x' ? 'x' : 'z';
  const signs: Array<1 | -1> =
    !faces || faces === 'both' ? [1, -1] : faces.startsWith('+') ? [1] : [-1];
  const runLength = axis === 'z' ? width : depth;

  return (
    <>
      {signs.map((sign) => {
        const rotationY =
          axis === 'z' ? (sign === 1 ? -Math.PI / 2 : Math.PI / 2) : sign === 1 ? 0 : Math.PI;
        const position: [number, number, number] =
          axis === 'z'
            ? [sign === 1 ? runLength / 2 : -runLength / 2, baseY, (sign * depth) / 2]
            : [(sign * width) / 2, baseY, sign === 1 ? -runLength / 2 : runLength / 2];
        return (
          <mesh key={sign} position={position} rotation={[0, rotationY, 0]} castShadow receiveShadow>
            <extrudeGeometry args={[shape, { depth: runLength, bevelEnabled: false, curveSegments: 12 }]} />
            <meshStandardMaterial color={color} roughness={roughness} metalness={metalness} />
          </mesh>
        );
      })}
    </>
  );
}

function WallSegment({ wall }: { wall: MapWallSegment }) {
  const baseColor = wall.color ?? NEUTRAL_WALL;
  // A wall's visible face is width x height; repeats follow that, not the slab
  // depth, so a texture reads at the same density on every wall.
  const material = surfaceMaterial({
    surface: wall.surface,
    fallbackColor: baseColor,
    worldSize: [wall.size[0], wall.size[1]],
  });
  return (
    <group position={wall.position} rotation={[0, wall.rotationY ?? 0, 0]}>
      <mesh castShadow receiveShadow material={material}>
        <boxGeometry args={wall.size} />
      </mesh>
      <WallTrim wall={wall} baseColor={wall.surface?.color ?? baseColor} />
    </group>
  );
}

function FloorPanelMesh({ panel }: { panel: MapFloorPanel }) {
  const material = surfaceMaterial({
    surface: panel.surface,
    fallbackColor: panel.color || NEUTRAL_FLOOR,
    worldSize: panel.size,
  });
  return (
    <mesh position={[panel.center[0], -0.05, panel.center[1]]} receiveShadow material={material}>
      <boxGeometry args={[panel.size[0], 0.1, panel.size[1]]} />
    </mesh>
  );
}

/**
 * Ceiling slab for a panel that asks for one. The slab's underside sits a hair
 * above `ceilingHeight` so wall tops are buried inside it rather than meeting
 * it coplanar (which z-fights).
 */
function CeilingPanelMesh({ panel }: { panel: MapFloorPanel }) {
  const ceiling = panel.ceiling;
  if (!ceiling) return null;
  const material = surfaceMaterial({
    surface: ceiling,
    fallbackColor: ceiling.color ?? '#1b2026',
    worldSize: panel.size,
  });
  return (
    <mesh
      position={[panel.center[0], panel.ceilingHeight + 0.3, panel.center[1]]}
      receiveShadow
      material={material}
    >
      <boxGeometry args={[panel.size[0], 0.5, panel.size[1]]} />
    </mesh>
  );
}

function FloorGroup({
  floor,
  floorOffsetY,
  instances,
  lights,
  triggers,
  decals,
  markers,
  editable,
  runtimeState,
  showProps,
  showLights,
  showTriggers,
  showDecals,
  showMarkers,
  showCeilings,
}: {
  floor: MapFloorDocument;
  floorOffsetY: number;
  instances: MapObjectInstance[];
  lights: MapLight[];
  triggers: MapTriggerVolume[];
  decals: MapDecal[];
  markers: MapRoomMarker[];
  editable: boolean;
  runtimeState: AssetRuntimeState;
  showProps: boolean;
  showLights: boolean;
  showTriggers: boolean;
  showDecals: boolean;
  showMarkers: boolean;
  showCeilings: boolean;
}) {
  const selectedObjectIds = useMapEditorStore((state) => state.selectedObjectIds);
  const hoveredObjectId = useMapEditorStore((state) => state.hoveredObjectId);
  const pendingAssetId = useMapEditorStore((state) => state.pendingAssetId);
  const placementRotationY = useMapEditorStore((state) => state.placementRotationY);
  const placementPreview = useMapEditorStore((state) => state.placementPreview);
  const isMultiSelect = selectedObjectIds.length > 1;

  return (
    <group position={[0, floorOffsetY, 0]}>
      {floor.panels.map((panel) => (
        <FloorPanelMesh key={panel.id} panel={panel} />
      ))}
      {showCeilings
        ? floor.panels.map((panel) => (
            <CeilingPanelMesh key={`${panel.id}-ceiling`} panel={panel} />
          ))
        : null}
      {floor.walls.map((wall) => (
        <WallSegment key={wall.id} wall={wall} />
      ))}

      {showProps && instances.map((instance) => (
        <SceneObject
          key={instance.id}
          instance={instance}
          floorOffsetY={floorOffsetY}
          editable={editable}
          selected={selectedObjectIds.includes(instance.id)}
          isMultiSelect={isMultiSelect}
          hovered={hoveredObjectId === instance.id}
          runtimeState={runtimeState}
        />
      ))}

      {showLights && lights.map((light) => (
        <SceneLight
          key={light.id}
          light={light}
          editable={editable}
          selected={selectedObjectIds.includes(light.id)}
          hovered={hoveredObjectId === light.id}
        />
      ))}

      {showTriggers && triggers.map((trigger) => (
        <SceneTrigger
          key={trigger.id}
          trigger={trigger}
          editable={editable}
          selected={selectedObjectIds.includes(trigger.id)}
          hovered={hoveredObjectId === trigger.id}
        />
      ))}

      {showDecals && decals.map((decal) => (
        <SceneDecal
          key={decal.id}
          decal={decal}
          editable={editable}
          selected={selectedObjectIds.includes(decal.id)}
          hovered={hoveredObjectId === decal.id}
        />
      ))}

      {showMarkers && markers.map((marker) => (
        <SceneMarker
          key={marker.id}
          marker={marker}
          editable={editable}
          selected={selectedObjectIds.includes(marker.id)}
          hovered={hoveredObjectId === marker.id}
        />
      ))}

      {editable
        ? floor.panels.length > 0
          ? floor.panels.map((panel) => (
              <PlacementSurface key={`${panel.id}-surface`} floor={floor.level} panel={panel} />
            ))
          : (
              // Fallback: empty floor has no panels, but we still need a surface
              // so the user can click-to-place. Render a large invisible plane.
              <PlacementSurface
                key="fallback-surface"
                floor={floor.level}
                panel={{ id: 'fallback', center: [0, 0], size: [100, 100], color: NEUTRAL_FLOOR, ceilingHeight: 3 }}
              />
            )
        : null}

      {editable && pendingAssetId && placementPreview?.floor === floor.level ? (
        <GhostPreviewProp
          assetId={pendingAssetId}
          position={placementPreview.position}
          rotationY={placementRotationY}
          runtimeState={runtimeState}
        />
      ) : null}
    </group>
  );
}

export interface LevelRendererProps {
  mapDocument: MapDocument;
  mode: 'play' | 'edit';
  /**
   * Play mode only: read object instances from the live, mutable
   * `liveSceneStore` (a real running `GameRuntime` session, which is the
   * only thing that ever seeds it) instead of statically from
   * `mapDocument.objectInstances`. Defaults to `false` - a `mode: 'play'`
   * render with no live session actually behind it (e.g.
   * `MapStructureViewport`'s blueprint-editing viewport below, which
   * renders `LevelRenderer` directly for its "show existing props while
   * drawing walls" context and never mounts a `GameRuntime`) must keep
   * reading the authored document, exactly as every mode did before the
   * live scene store existed - otherwise it silently shows whatever
   * `liveSceneStore` happens to hold from an unrelated, possibly stale or
   * empty, Preview session elsewhere in the same tab. `GameRuntime` is the
   * only caller that passes `true`.
   */
  useLiveScene?: boolean;
  /** Floor-height options (shared with deriveMapData for consistent geometry). */
  runtimeOptions?: MapRuntimeOptions;
  /** Opaque per-object runtime bag forwarded to renderers (play mode). */
  runtimeState?: AssetRuntimeState;
  /**
   * Render the ceiling slab of every panel that authors one. Defaults to true
   * in play mode and false in edit mode, where a lid would hide the rooms the
   * author is working in.
   */
  showCeilings?: boolean;
  /** The project's character roster, resolved against `mapDocument.characterInstances`'
   * `characterId`. Omitted (or a dangling id) renders no placed characters,
   * the same "skip rather than crash" convention a dangling `spriteId`
   * already gets in the 2D runtime. */
  characters?: CharacterDefinition[];
  /** Pose libraries for `characters`, so a placed character's idle/walk
   * pose (see `PlacedCharacters`) can resolve to real bone transforms. */
  poseLibraries?: CharacterPoseLibrary[];
  /** Forwarded straight to `PlacedCharacters` - see its own doc comment. */
  cutsceneActorsRef?: MutableRefObject<Record<string, CutsceneActor>>;
}

/**
 * Registry-driven scene renderer. Renders every floor's panels/walls + the
 * lights and object instances from a MapDocument. In `edit` mode object
 * instances come from the live `mapEditorStore` (selectable/draggable, with
 * placement surfaces + ghost preview); in `play` mode with `useLiveScene`
 * they come from `liveSceneStore`, seeded by `GameRuntime` from the document
 * once per play session - a running scene can be spawned/moved/removed into
 * (e.g. by a HollowScript `entities` capability call) without ever touching
 * the authored document. Without `useLiveScene` (its default), `play` mode
 * still renders straight from the document, unchanged from before that
 * store existed - see `useLiveScene`'s own doc comment for why a `play`-mode
 * render with no real `GameRuntime` behind it needs this. Placed characters
 * (`characterInstances`) always render straight from the document in play
 * mode - only object instances have a live runtime store so far.
 * `PlacedCharacters` itself is unchanged either way - only which array feeds
 * its `instances` prop differs.
 */
export function LevelRenderer({
  mapDocument,
  mode,
  useLiveScene = false,
  runtimeOptions,
  runtimeState,
  showCeilings,
  characters,
  poseLibraries,
  cutsceneActorsRef,
}: LevelRendererProps) {
  const editable = mode === 'edit';
  const ceilingsVisible = showCeilings ?? !editable;
  // Definitions can register after the scene mounts (async packs like the
  // shared library); the version subscription re-renders placed instances
  // whose assets just became known.
  useSyncExternalStore(subscribeObjectRegistry, getObjectRegistryVersion);
  const editorInstances = useMapEditorStore((state) => state.objectInstances);
  const editorLights = useMapEditorStore((state) => state.lights);
  const editorTriggers = useMapEditorStore((state) => state.triggers);
  const editorDecals = useMapEditorStore((state) => state.decals);
  const editorMarkers = useMapEditorStore((state) => state.markers);
  const editorCharacterInstances = useMapEditorStore((state) => state.characterInstances);
  const selectedObjectIds = useMapEditorStore((state) => state.selectedObjectIds);
  const hoveredObjectId = useMapEditorStore((state) => state.hoveredObjectId);
  const visibilityToggles = useMapEditorStore((state) => state.visibilityToggles);
  // Always subscribed (hooks can't be conditional), but only actually used
  // as instancesByFloor's source when useLiveScene is true - see that prop's
  // own doc comment for why a mode: 'play' render with no real GameRuntime
  // session behind it (MapStructureViewport below) must not read this.
  const liveObjectInstances = useLiveSceneStore((state) => state.objectInstances);
  const currentFloor = useEngineRuntimeStore((state) => state.currentFloor);
  const rs = runtimeState ?? {};

  const instancesByFloor = useMemo(() => {
    const source = editable ? editorInstances : useLiveScene ? liveObjectInstances : mapDocument.objectInstances;
    const map = new Map<MapFloorLevel, MapObjectInstance[]>();
    for (const instance of source) {
      const list = map.get(instance.floor) ?? [];
      list.push(instance);
      map.set(instance.floor, list);
    }
    return map;
  }, [editable, editorInstances, useLiveScene, liveObjectInstances, mapDocument.objectInstances]);

  const lights = useMemo(() => (editable ? editorLights : (mapDocument.lights ?? [])), [editable, editorLights, mapDocument.lights]);
  const triggers = useMemo(() => (editable ? editorTriggers : (mapDocument.triggers ?? [])), [editable, editorTriggers, mapDocument.triggers]);
  const decals = useMemo(() => (editable ? editorDecals : (mapDocument.decals ?? [])), [editable, editorDecals, mapDocument.decals]);
  const markers = useMemo(() => (editable ? editorMarkers : (mapDocument.markers ?? [])), [editable, editorMarkers, mapDocument.markers]);
  const characterInstances = useMemo(
    () => (editable ? editorCharacterInstances : (mapDocument.characterInstances ?? [])),
    [editable, editorCharacterInstances, mapDocument.characterInstances],
  );
  const characterRoster = useMemo(() => characters ?? [], [characters]);
  const characterPoseLibraries = poseLibraries ?? [];
  const characterById = useMemo(
    () => new Map(characterRoster.map((c) => [c.id, c])),
    [characterRoster],
  );

  const showProps = !editable || visibilityToggles.props;
  const showLights = !editable || visibilityToggles.lights;
  const showTriggers = !editable || visibilityToggles.triggers;
  const showDecals = !editable || visibilityToggles.decals;
  const showMarkers = !editable || visibilityToggles.markers;

  const docFloorLevels = useMemo(
    () => new Set(mapDocument.floors.map((floor) => floor.level)),
    [mapDocument.floors],
  );
  const orphanFloors = useMemo(() => {
    // A floor level can carry lights/triggers/decals/markers with no object
    // instance on it at all (or an instance-bearing floor's document entry
    // can be deleted/renamed) - every collection's own floor levels need to
    // be checked here, not just instancesByFloor's, or that content renders
    // nowhere with no indication why.
    const levels = new Set(instancesByFloor.keys());
    for (const l of lights) levels.add(l.floor);
    for (const t of triggers) levels.add(t.floor);
    for (const d of decals) levels.add(d.floor);
    for (const m of markers) levels.add(m.floor);
    return [...levels].filter((level) => !docFloorLevels.has(level));
  }, [instancesByFloor, lights, triggers, decals, markers, docFloorLevels]);

  // A live play session (useLiveScene, only ever true from GameRuntime with a
  // real FirstPersonController driving engineRuntimeStore's currentFloor) only
  // ever needs the current floor plus its immediate vertical neighbours (see
  // isFloorLevelVisible) - rendering every storey's walls/panels/props every
  // frame regardless of which one the player is on costs real draw calls
  // and, more importantly, real shadow-map rasterisation cost on every
  // real-time shadow-casting light for geometry the player cannot currently
  // see or reach.
  //
  // Not applied to `editable` (the map editor always shows every floor for
  // authoring) or to any other `mode: 'play'` caller that leaves
  // `useLiveScene` at its default `false` (e.g. MapStructureScene below,
  // which pre-filters `mapDocument` to a single arbitrary floor for its
  // top-down authoring view - that floor is unrelated to whatever
  // `currentFloor` a live session elsewhere happens to hold, so culling here
  // too could hide the very floor it asked to show).
  const floorCullingActive = useLiveScene;
  const isFloorVisible = useCallback(
    (level: MapFloorLevel) =>
      !floorCullingActive || isFloorLevelVisible(level, currentFloor, runtimeOptions),
    [floorCullingActive, currentFloor, runtimeOptions],
  );
  const visibleFloors = useMemo(
    () => mapDocument.floors.filter((floor) => isFloorVisible(floor.level)),
    [mapDocument.floors, isFloorVisible],
  );
  const visibleOrphanFloors = useMemo(
    () => orphanFloors.filter((level) => isFloorVisible(level)),
    [orphanFloors, isFloorVisible],
  );

  return (
    <>
      {editable ? <DropPlacementController runtimeOptions={runtimeOptions} /> : null}
      {visibleFloors.map((floor) => (
        <FloorGroup
          key={floor.id}
          floor={floor}
          floorOffsetY={floorWorldY(floor.level, runtimeOptions)}
          instances={instancesByFloor.get(floor.level) ?? []}
          lights={lights.filter(l => l.floor === floor.level)}
          triggers={triggers.filter(t => t.floor === floor.level)}
          decals={decals.filter(d => d.floor === floor.level)}
          markers={markers.filter(m => m.floor === floor.level)}
          editable={editable}
          runtimeState={rs}
          showProps={showProps}
          showLights={showLights}
          showTriggers={showTriggers}
          showDecals={showDecals}
          showMarkers={showMarkers}
          showCeilings={ceilingsVisible}
        />
      ))}
      {visibleOrphanFloors.map((level) => (
        <group key={`orphan-${level}`} position={[0, floorWorldY(level, runtimeOptions), 0]}>
          {showProps && (instancesByFloor.get(level) ?? []).map((instance) => (
            <SceneObject
              key={instance.id}
              instance={instance}
              floorOffsetY={floorWorldY(level, runtimeOptions)}
              editable={editable}
              selected={false}
              isMultiSelect={false}
              hovered={false}
              runtimeState={rs}
            />
          ))}
          {showLights && lights.filter(l => l.floor === level).map((light) => (
            <SceneLight
              key={light.id}
              light={light}
              editable={editable}
              selected={false}
              hovered={false}
            />
          ))}
          {showTriggers && triggers.filter(t => t.floor === level).map((trigger) => (
            <SceneTrigger
              key={trigger.id}
              trigger={trigger}
              editable={editable}
              selected={false}
              hovered={false}
            />
          ))}
          {showDecals && decals.filter(d => d.floor === level).map((decal) => (
            <SceneDecal
              key={decal.id}
              decal={decal}
              editable={editable}
              selected={false}
              hovered={false}
            />
          ))}
          {showMarkers && markers.filter(m => m.floor === level).map((marker) => (
            <SceneMarker
              key={marker.id}
              marker={marker}
              editable={editable}
              selected={false}
              hovered={false}
            />
          ))}
        </group>
      ))}
      <PlacedCharacters
        instances={characterInstances}
        characters={characterRoster}
        poseLibraries={characterPoseLibraries}
        floorOffsetY={(floor) => floorWorldY(floor, runtimeOptions)}
        cutsceneActorsRef={cutsceneActorsRef}
      />

      {/* ── Character selection + TransformControls (edit mode) ────────────
          Additive sibling to PlacedCharacters above: same instances, same
          floor offset, but only the interaction layer (see
          SceneCharacterSelection's own banner comment for why this isn't
          folded into PlacedCharacter itself). */}
      {editable
        ? characterInstances.map((instance) => {
            const definition = characterById.get(instance.characterId);
            if (!definition) return null;
            return (
              <SceneCharacterSelection
                key={instance.id}
                instance={instance}
                name={definition.name}
                floorOffsetY={floorWorldY(instance.floor, runtimeOptions)}
                selected={selectedObjectIds.includes(instance.id)}
                hovered={hoveredObjectId === instance.id}
              />
            );
          })
        : null}
      {/* ── End character selection + TransformControls ────────────────── */}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ── Map Builder: ortho top-down structure-editing viewport ─────────────────
//
// Replaces the old bespoke SVG/DOM blueprint editor (screens-editor.tsx's
// MapRoute): a real orthographic top-down camera over the shared
// `LevelRenderer` scene, restricted to the floor being edited (stacking
// every floor under a straight-down camera would overlap them in
// projection, unlike the old plan view which only ever drew one floor).
// Wall/room/stair *authoring logic* (deriveFloorPatch, wallFromDrag,
// panelFromDrag, stairFootprintFromDrag, hitTestStructure) stays in
// screens-editor.tsx untouched - this layer only supplies the render
// surface and turns pointer events into ground-plane world coordinates via
// ordinary R3F raycasting (`event.point`), replacing the old
// worldFromCanvas/canvasFromWorld pixel math.
//
// Entirely additive: nothing above this line is touched, so this block can
// be reviewed/merged independently of any concurrent edit to
// `LevelRenderer`'s own render path above.
// ═════════════════════════════════════════════════════════════════════════

export type MapStructurePreview =
  | { kind: 'wall'; wall: MapWallSegment }
  | { kind: 'panel' | 'room'; panel: MapFloorPanel }
  | { kind: 'stairs'; footprint: StairFootprint };

const STRUCTURE_PREVIEW_COLOR = '#7fe3ff';
const ROOM_PREVIEW_COLOR = '#6b5bd6';
const STAIR_RUN_COLOR = '#d6a94b';
const STAIR_TRIGGER_COLOR = '#4bc9d6';
const ROOM_SELECTED_COLOR = '#ffcf6b';

/** A large invisible ground-plane at the active floor's own height, purely a
 * raycast target: every Map Builder tool (wall/room/stairs/erase) reads
 * world XZ off `event.point`, matching how `PlacementSurface` above already
 * turns pointer events into world coordinates. */
function StructureDrawSurface({
  size,
  onDown,
  onMove,
  onUp,
}: {
  size: number;
  onDown: (point: [number, number], shiftKey: boolean) => void;
  onMove: (point: [number, number], shiftKey: boolean) => void;
  onUp: () => void;
}) {
  return (
    <mesh
      position={[0, 0.05, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      onPointerDown={(event) => {
        event.stopPropagation();
        (event.target as Element | null)?.setPointerCapture?.(event.pointerId);
        onDown([event.point.x, event.point.z], event.nativeEvent.shiftKey);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        onMove([event.point.x, event.point.z], event.nativeEvent.shiftKey);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        (event.target as Element | null)?.releasePointerCapture?.(event.pointerId);
        onUp();
      }}
    >
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

/** Live drag preview: a wall/panel/room footprint or a stair footprint,
 * translucent so the structure underneath still reads. Mirrors the old
 * SVG editor's dashed-preview rectangles, in mesh form. */
function StructurePreviewMesh({
  preview,
  stairKind,
}: {
  preview: MapStructurePreview;
  stairKind: 'run' | 'trigger';
}) {
  if (preview.kind === 'wall') {
    const { wall } = preview;
    const length = Math.max(wall.size[0], wall.size[2]);
    return (
      <group>
        <mesh position={wall.position} rotation={[0, wall.rotationY ?? 0, 0]}>
          <boxGeometry args={wall.size} />
          <meshBasicMaterial color={STRUCTURE_PREVIEW_COLOR} transparent opacity={0.55} depthTest={false} />
        </mesh>
        <Html
          position={[wall.position[0], wall.position[1] + wall.size[1] / 2 + 0.3, wall.position[2]]}
          center
          style={{ pointerEvents: 'none' }}
        >
          <div className="editor-floating-label" style={{ borderLeftColor: STRUCTURE_PREVIEW_COLOR }}>
            <span className="editor-floating-label-name">{length.toFixed(1)} m</span>
          </div>
        </Html>
      </group>
    );
  }
  if (preview.kind === 'stairs') {
    const { footprint } = preview;
    const color = stairKind === 'run' ? STAIR_RUN_COLOR : STAIR_TRIGGER_COLOR;
    return (
      <group position={[footprint.center[0], 0.06, footprint.center[1]]}>
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={footprint.size} />
          <meshBasicMaterial color={color} transparent opacity={0.3} depthTest={false} depthWrite={false} />
        </mesh>
        <OutlineBox size={[footprint.size[0], 0.02, footprint.size[1]]} color={color} opacity={0.9} />
      </group>
    );
  }
  // The remaining case has a two-literal discriminant ('panel' | 'room') on
  // one union member - narrowed here by construction (only 'wall'/'stairs'
  // were eliminated above), rather than by testing it directly, since TS
  // does not always narrow away a union-valued discriminant by elimination.
  const { panel } = preview;
  const color = preview.kind === 'room' ? ROOM_PREVIEW_COLOR : STRUCTURE_PREVIEW_COLOR;
  return (
    <group position={[panel.center[0], 0.06, panel.center[1]]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={panel.size} />
        <meshBasicMaterial color={color} transparent opacity={0.28} depthTest={false} depthWrite={false} />
      </mesh>
      <OutlineBox size={[panel.size[0], 0.02, panel.size[1]]} color={color} opacity={0.9} />
    </group>
  );
}

/** A floor panel's room name (or its raw id for a hand-drawn panel), floated
 * just above the panel - the top-down equivalent of the old plan view's
 * corner label. */
function StructurePanelLabel({ panel, room }: { panel: MapFloorPanel; room?: MapRoomDraft }) {
  return (
    <Html position={[panel.center[0], 0.3, panel.center[1]]} center style={{ pointerEvents: 'none' }} zIndexRange={[5, 0]}>
      <div className="editor-floating-label">
        <span className="editor-floating-label-name">{room?.name ?? panel.id}</span>
      </div>
    </Html>
  );
}

/** Outline around the selected room's own extents (not its derived panel -
 * the room draft is the source of truth and always available, even for a
 * brand-new room whose derived panel hasn't round-tripped through a save
 * yet). */
function StructureRoomHighlight({ room }: { room: MapRoomDraft }) {
  const center: [number, number] = [(room.x[0] + room.x[1]) / 2, (room.z[0] + room.z[1]) / 2];
  const size: [number, number] = [room.x[1] - room.x[0], room.z[1] - room.z[0]];
  return (
    <group position={[center[0], 0.08, center[1]]}>
      <OutlineBox size={[size[0], 0.02, size[1]]} color={ROOM_SELECTED_COLOR} opacity={0.9} />
    </group>
  );
}

function StructureStairRunMarker({ run }: { run: MapStairRun }) {
  return (
    <group position={[run.center[0], 0.04, run.center[1]]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={run.size} />
        <meshBasicMaterial color={STAIR_RUN_COLOR} transparent opacity={0.22} depthWrite={false} />
      </mesh>
      <OutlineBox size={[run.size[0], 0.02, run.size[1]]} color={STAIR_RUN_COLOR} opacity={0.8} />
      <Html position={[0, 0.4, 0]} center style={{ pointerEvents: 'none' }}>
        <div className="editor-floating-label" style={{ borderLeftColor: STAIR_RUN_COLOR }}>
          <span className="editor-floating-label-name">Stair run to floor {run.toFloor}</span>
        </div>
      </Html>
    </group>
  );
}

function StructureStairTriggerMarker({ trigger }: { trigger: MapStairTrigger }) {
  return (
    <group position={[trigger.center[0], 0.04, trigger.center[1]]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={trigger.size} />
        <meshBasicMaterial color={STAIR_TRIGGER_COLOR} transparent opacity={0.22} depthWrite={false} />
      </mesh>
      <OutlineBox size={[trigger.size[0], 0.02, trigger.size[1]]} color={STAIR_TRIGGER_COLOR} opacity={0.8} />
      <Html position={[0, 0.4, 0]} center style={{ pointerEvents: 'none' }}>
        <div className="editor-floating-label" style={{ borderLeftColor: STAIR_TRIGGER_COLOR }}>
          <span className="editor-floating-label-name">Stair trigger to floor {trigger.toFloor}</span>
        </div>
      </Html>
    </group>
  );
}

export interface MapStructureViewportProps {
  /** The project's full map document - only `floors[activeLevel]` and the
   * top-level stair collections are actually rendered; passed whole so the
   * viewport can do that filtering itself rather than pushing it onto every
   * caller. */
  mapDocument: MapDocument;
  activeLevel: MapFloorLevel;
  /** World metres from plan centre to the nearest edge - same "centred,
   * fit-to-content" framing `floorExtent`/`blueprintScale` already define
   * for the old SVG viewport, reused verbatim for the camera's zoom. */
  extent: number;
  gridSnap: number;
  selectedRoomId: string | null;
  stairKind: 'run' | 'trigger';
  preview: MapStructurePreview | null;
  onPointerDownWorld: (point: [number, number], shiftKey: boolean) => void;
  onPointerMoveWorld: (point: [number, number], shiftKey: boolean) => void;
  onPointerUpWorld: () => void;
}

function MapStructureScene({
  mapDocument,
  activeLevel,
  extent,
  gridSnap,
  selectedRoomId,
  stairKind,
  preview,
  onPointerDownWorld,
  onPointerMoveWorld,
  onPointerUpWorld,
}: MapStructureViewportProps) {
  const { size } = useThree();
  const floorY = floorWorldY(activeLevel);
  const zoom = blueprintScale({ width: size.width, height: size.height, extent });

  // A straight-down camera projects every floor onto the same plane, so only
  // the floor being edited is ever handed to LevelRenderer here - showing
  // every storey stacked on top of each other would be unreadable, and the
  // old plan view only ever drew one floor too. Every floor-tagged collection
  // is filtered the same way, not just `floors`: LevelRenderer's own
  // "orphan floor" fallback renders any instance/light/trigger/decal/marker
  // whose floor isn't among the document's floors at all, which - left
  // unfiltered here - would render every OTHER floor's content back in via
  // that fallback instead of the (deliberately avoided) main path.
  const singleFloorDocument = useMemo<MapDocument>(
    () => ({
      ...mapDocument,
      floors: mapDocument.floors.filter((f) => f.level === activeLevel),
      objectInstances: mapDocument.objectInstances.filter((i) => i.floor === activeLevel),
      lights: (mapDocument.lights ?? []).filter((l) => l.floor === activeLevel),
      triggers: (mapDocument.triggers ?? []).filter((t) => t.floor === activeLevel),
      decals: (mapDocument.decals ?? []).filter((d) => d.floor === activeLevel),
      markers: (mapDocument.markers ?? []).filter((m) => m.floor === activeLevel),
      characterInstances: (mapDocument.characterInstances ?? []).filter((c) => c.floor === activeLevel),
    }),
    [mapDocument, activeLevel],
  );
  const activeFloor = singleFloorDocument.floors[0] ?? null;
  const selectedRoom = activeFloor?.rooms?.find((r) => r.id === selectedRoomId) ?? null;
  const stairRunsHere = useMemo(
    () => (mapDocument.stairRuns ?? []).filter((r) => r.fromFloor === activeLevel),
    [mapDocument.stairRuns, activeLevel],
  );
  const stairTriggersHere = useMemo(
    () => mapDocument.stairTriggers.filter((t) => t.fromFloor === activeLevel),
    [mapDocument.stairTriggers, activeLevel],
  );
  const drawSurfaceSize = Math.max(extent * 4, 200);
  const step = gridSnap > 0 ? gridSnap : 0.5;
  const gridDivisions = Math.max(1, Math.round((extent * 2) / step));

  return (
    <>
      <ambientLight intensity={0.95} />
      <directionalLight position={[20, 60, 20]} intensity={0.5} />
      <OrthographicCamera
        makeDefault
        manual
        up={[0, 0, -1]}
        position={[0, floorY + 80, 0]}
        left={-size.width / 2}
        right={size.width / 2}
        top={size.height / 2}
        bottom={-size.height / 2}
        near={0.1}
        far={500}
        zoom={zoom}
        onUpdate={(self) => {
          self.updateProjectionMatrix();
          self.lookAt(0, floorY, 0);
        }}
      />
      <LevelRenderer mapDocument={singleFloorDocument} mode="play" showCeilings={false} runtimeState={{}} />
      <group position={[0, floorY, 0]}>
        <gridHelper args={[extent * 2, gridDivisions, '#3a4048', '#22262b']} />
        <StructureDrawSurface size={drawSurfaceSize} onDown={onPointerDownWorld} onMove={onPointerMoveWorld} onUp={onPointerUpWorld} />
        {activeFloor?.panels.map((panel) => (
          <StructurePanelLabel key={panel.id} panel={panel} room={activeFloor.rooms?.find((r) => r.id === panel.id)} />
        ))}
        {selectedRoom ? <StructureRoomHighlight room={selectedRoom} /> : null}
        {stairRunsHere.map((run) => (
          <StructureStairRunMarker key={run.id} run={run} />
        ))}
        {stairTriggersHere.map((trigger) => (
          <StructureStairTriggerMarker key={trigger.id} trigger={trigger} />
        ))}
        {preview ? <StructurePreviewMesh preview={preview} stairKind={stairKind} /> : null}
      </group>
    </>
  );
}

/**
 * Map Builder's viewport: a real orthographic top-down camera over the
 * shared `LevelRenderer` scene, restricted to the active floor. Projects
 * into the shared `<ViewportHost>` canvas via `<Viewport>` (see
 * ./viewport/Viewport.tsx), the same pattern GameRuntime/CharacterStudio/the
 * gallery stages already use, so this viewport no longer owns a second raw
 * `<Canvas>` alongside the shared one - falls back to owning its own Canvas
 * only when mounted with no `<ViewportHost>` ancestor (a standalone
 * `@sstk/build-kit` consumer). The caller (screens-editor.tsx's MapRoute)
 * only supplies tool state and the pointer callbacks that drive its own
 * unchanged
 * wallFromDrag/panelFromDrag/stairFootprintFromDrag/deriveFloorPatch
 * pipeline, exactly as the old SVG viewport did with worldFromCanvas - only
 * how it renders moved, not the geometry pipeline itself.
 */
export function MapStructureViewport(props: MapStructureViewportProps) {
  return (
    <Viewport gl={{ antialias: true }} style={{ width: '100%', height: '100%' }}>
      <MapStructureScene {...props} />
    </Viewport>
  );
}
